import React, { forwardRef, useState, useImperativeHandle, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield } from "lucide-react";
import MicrosoftAuthForm from "./MicrosoftAuthForm";
import GoogleAuthForm from "./GoogleAuthForm";

type AuthProvider = "microsoft" | "google"; // | "github" - for future providers

type Props = {
    data?: any;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

const AUTH_PROVIDERS: {
    label: string;
    value: AuthProvider;
    image: string;
    description: string;
    enabled: boolean;
}[] = [
    {
        label: "Microsoft",
        value: "microsoft",
        image: "/assets/providers/microsoft.svg", // You may need to add this image
        description: "Microsoft Azure Active Directory authentication",
        enabled: true,
    },
    {
        label: "Google",
        value: "google",
        image: "/assets/providers/google.svg", // You may need to add this image
        description: "Google OAuth authentication",
        enabled: true,
    },
];

const AuthProviderForm = forwardRef<any, Props>(({ data, initialData = {}, onDataChange, onSubmit, ...props }, ref) => {
    const [authProvider, setAuthProvider] = useState<AuthProvider>(
        initialData?.AUTH_PROVIDER || data?.AUTH_PROVIDER || "microsoft"
    );

    const formRef = useRef<any>(null);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        validateForm: async () => {
            if (formRef.current && formRef.current.validateForm) {
                return await formRef.current.validateForm();
            }
            return true;
        },
        getFormData: () => {
            const providerData = { AUTH_PROVIDER: authProvider };
            if (formRef.current && formRef.current.getFormData) {
                return {
                    ...providerData,
                    ...formRef.current.getFormData(),
                };
            }
            return providerData;
        },
        submitForm: () => {
            if (formRef.current && formRef.current.submitForm && onSubmit) {
                formRef.current.submitForm();
            }
        },
    }));

    // Update parent component when auth provider changes (removed useEffect to prevent infinite rerenders)

    const handleDataChange = (formData: any) => {
        if (onDataChange) {
            onDataChange({
                ...formData,
                AUTH_PROVIDER: authProvider,
            });
        }
    };

    const handleSubmit = (_data: any) => {
        if (!onSubmit) return;

        const __data = {
            ...data,
            ..._data,
            AUTH_PROVIDER: authProvider,
        };

        onSubmit(__data);
    };

    const renderForm = (authProvider: AuthProvider) => {
        const formProps = {
            ...props,
            onSubmit: handleSubmit,
            data: {
                ...data,
                ...initialData,
            },
            onDataChange: handleDataChange,
        };

        switch (authProvider) {
            case "microsoft":
                return <MicrosoftAuthForm {...formProps} ref={formRef} />;
            case "google":
                return <GoogleAuthForm {...formProps} ref={formRef} />;
            // Future providers:
            // case "github":
            //     return <GitHubAuthForm {...formProps} ref={formRef} />;
            default:
                return <MicrosoftAuthForm {...formProps} ref={formRef} />;
        }
    };

    const enabledProviders = AUTH_PROVIDERS.filter((provider) => provider.enabled);

    return (
        <div className="space-y-6">
            {/* Provider Selection */}
            <div className="space-y-2">
                <Label htmlFor="auth-provider">Authentication Provider</Label>
                <Select
                    defaultValue={authProvider}
                    onValueChange={(value: AuthProvider) => {
                        setAuthProvider(value);
                        if (onDataChange) {
                            onDataChange({
                                ...initialData,
                                AUTH_PROVIDER: value,
                            });
                        }
                    }}
                >
                    <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select Authentication Provider" id="auth-provider" />
                    </SelectTrigger>
                    <SelectContent>
                        {enabledProviders.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                                <div className="flex items-center gap-2">
                                    <Shield className="h-4 w-4" />
                                    <div className="text-start">
                                        <div className="font-medium">{item.label}</div>
                                        <div className="text-xs text-muted-foreground">{item.description}</div>
                                    </div>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Provider-specific Form */}
            <div className="space-y-4">{renderForm(authProvider)}</div>
        </div>
    );
});

AuthProviderForm.displayName = "AuthProviderForm";

export default AuthProviderForm;
